/**
 * The article list.
 *
 * Three decisions in here are worth reading, because each is a place where the
 * obvious version is worse.
 *
 * **The search is debounced and the request is cancellable by supersession.**
 * The catalogue is 32 086 articles and somebody typing "plaquette" issues nine
 * requests; without the debounce the server does nine full-text scans, and
 * without `useResource`'s supersession guard the list settles on whichever
 * finished last rather than on what is in the box.
 *
 * **Pagination is by cursor, not by page number.** The API pages by keyset
 * because `OFFSET` at row 30 000 reads 30 000 rows to discard them. That means
 * "next page" and "start again", not "page 7" — so the screen offers exactly
 * those and shows the total separately.
 *
 * **Completion is a meter, not a number.** It answers "is this article ready
 * to publish", which is a comparison across rows, and a column of percentages
 * makes the reader do the comparison themselves.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { useCan } from "@hardauto/core-access";
import type { ApiClient } from "@hardauto/core-api";
import { useAction, useDebounced, useResource } from "@hardauto/core-data";
import { quantity } from "@hardauto/core-i18n";
import {
  Button,
  Checkbox,
  DataTable,
  Input,
  Meter,
  MultiSelect,
  Page,
  Select,
  StatusDot,
  Toolbar,
  useToast,
  type Column,
} from "@hardauto/core-ui";

import { ReferentialApi, type ArticleRow, type PublicationStatus } from "../api";
import { NAMESPACE } from "../i18n";
import { REFERENTIAL_PATHS } from "../paths";
import type { ReferentialScope } from "../scope";
import "./referential.css";

const PAGE = 50;

/** The tone each publication state is drawn in. */
const STATUS_TONE: Record<PublicationStatus, "success" | "neutral" | "danger"> = {
  published: "success",
  draft: "neutral",
  archived: "danger",
};

export function ArticlesScreen({
  client,
  scope,
}: {
  client: ApiClient;
  /** The working-context bar's company, when the application injects one. */
  scope?: ReferentialScope;
}) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const api = useMemo(() => new ReferentialApi(client), [client]);
  const mayWrite = useCan("referential.article.write");

  const [term, setTerm] = useState("");
  // Each of these holds several values, which are alternatives.
  const [statuses, setStatuses] = useState<string[]>([]);
  // "true" / "false". Both ticked is the same as neither — no filter — so the
  // API only hears about it when exactly one is chosen.
  const [actives, setActives] = useState<string[]>([]);
  // Starts from the working context's company, and is this screen's own after.
  const [companies, setCompanies] = useState<string[]>(() =>
    scope?.companyCode ? [scope.companyCode] : [],
  );
  const toast = useToast();
  const action = useAction();
  // The ticked articles, for the bulk brand change. Cleared whenever the list
  // changes under them: a tick on a row that is no longer shown is a surprise.
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [bulkBrand, setBulkBrand] = useState("");
  // The cursor of the page being shown. Null is the first page.
  const [cursor, setCursor] = useState<string | null>(null);

  const search = useDebounced(term, 250);
  // The wording every multi-value filter of this screen shares.
  const multi = {
    countLabel: (count: number) => t("articles.selectedCount", { count }),
    clearLabel: t("articles.clearFilter"),
    searchLabel: t("articles.searchOptions"),
  };
  const companyCode = scope?.companyCode ?? "";
  const companiesFetcher = useCallback(() => api.listCompanies(true, false), [api]);
  const companyList = useResource(companiesFetcher);

  // The working context changes from outside this screen (the header's
  // "Change context"), so there is no local setter to route through
  // `narrow` — a plain effect is what notices it instead. Same reason as
  // `narrow`: a cursor from a wider list is a window into a list that no
  // longer exists under the narrower one.
  useEffect(() => {
    setCompanies(companyCode ? [companyCode] : []);
    setCursor(null);
  }, [companyCode]);

  const fetcher = useCallback(
    () =>
      api.listArticles({
        ...(search ? { search } : {}),
        statuses: statuses as PublicationStatus[],
        ...(actives.length === 1 ? { is_active: actives[0] === "true" } : {}),
        company_codes: companies,
        ...(cursor ? { after_id: cursor } : {}),
        limit: PAGE,
      }),
    [api, search, statuses, actives, companies, cursor],
  );
  const articles = useResource(fetcher);

  // Changing a filter must return to the first page: keeping a cursor from the
  // previous filter shows a window into a list that no longer exists.
  const narrow = useCallback((change: () => void) => {
    setCursor(null);
    setSelected(new Set());
    change();
  }, []);

  // The brand list, for the bulk change — only for somebody who may write.
  const brandsFetcher = useCallback(
    () => (mayWrite ? api.listBrandChoices().catch(() => []) : Promise.resolve([])),
    [api, mayWrite],
  );
  const brandChoices = useResource(brandsFetcher);

  const columns: Column<ArticleRow>[] = [
    ...(mayWrite
      ? [
          {
            key: "select",
            header: "",
            render: (row: ArticleRow) => (
              <Checkbox
                label={t("articles.select", { reference: row.article.reference })}
                checked={selected.has(row.article.id)}
                onChange={(event) => {
                  const next = new Set(selected);
                  if (event.target.checked) next.add(row.article.id);
                  else next.delete(row.article.id);
                  setSelected(next);
                }}
              />
            ),
          },
        ]
      : []),
    {
      key: "reference",
      header: t("articles.column.reference"),
      render: (row) => (
        <Link to={REFERENTIAL_PATHS.article.replace("$id", row.article.id)}>
          <span className="hardauto-ref">{row.article.reference}</span>
        </Link>
      ),
    },
    {
      key: "designation",
      header: t("articles.column.designation"),
      render: (row) => row.article.designation,
    },
    {
      key: "brand",
      header: t("articles.column.brand"),
      render: (row) => row.article.brand || "—",
    },
    {
      key: "family",
      header: t("articles.column.family"),
      render: (row) => (
        <span className={row.article.family_code ? undefined : "hardauto-cell__muted"}>
          {row.article.family_code || t("articles.unclassified")}
        </span>
      ),
    },
    {
      key: "stock",
      header: t("articles.column.stock"),
      render: (row) => (
        <span className="hardauto-num">{quantity(row.total_real, i18n.language)}</span>
      ),
    },
    {
      key: "completion",
      header: t("articles.column.completion"),
      render: (row) => (
        <Meter
          value={row.completion}
          label={t("articles.completionLabel")}
          tone={row.completion === 100 ? "success" : "accent"}
        />
      ),
    },
    {
      key: "publication",
      header: t("articles.column.publication"),
      render: (row) => (
        <StatusDot
          label={t(`article.status.${row.article.status}`)}
          tone={STATUS_TONE[row.article.status]}
        />
      ),
    },
    {
      key: "active",
      header: t("articles.column.active"),
      render: (row) =>
        mayWrite ? (
          // The same switch as on the article page, here so a whole list can
          // be put in or out of the catalogue without opening each article.
          <Select
            aria-label={`${t("article.active.label")} ${row.article.reference}`}
            value={row.article.is_active ? "active" : "inactive"}
            disabled={action.isBusy(`active:${row.article.id}`)}
            onChange={(event) => {
              const next = event.target.value === "active";
              void action.run(`active:${row.article.id}`, async () => {
                try {
                  await api.updateArticle(row.article.id, { is_active: next });
                  articles.reload();
                } catch {
                  toast.show(t("error.title"), { tone: "danger" });
                }
              });
            }}
          >
            <option value="active">{t("article.active.active")}</option>
            <option value="inactive">{t("article.active.inactive")}</option>
          </Select>
        ) : (
          <StatusDot
            label={t(`article.active.${row.article.is_active ? "active" : "inactive"}`)}
            tone={row.article.is_active ? "success" : "danger"}
          />
        ),
    },
  ];

  const rows = articles.data?.items ?? [];
  const total = articles.data?.total ?? 0;
  // A filtered list that comes back empty is a different message from an empty
  // referential: one says "widen the search", the other "create something".
  const isNarrowed = Boolean(search || statuses.length || actives.length === 1 || companies.length);

  return (
    <Page
      title={t("articles.title")}
      eyebrow={t("articles.count", { count: total })}
      actions={
        mayWrite ? (
          <Button variant="dark" disabled>
            {t("articles.import")}
          </Button>
        ) : undefined
      }
    >
      <Toolbar>
        <div className="hardauto-search">
          {/* `aria-label` rather than a `Field`: the design puts no visible
              label on a toolbar search box, and a `<label>` hidden with CSS
              would be a second way of doing what this attribute does. */}
          <Input
            type="search"
            aria-label={t("articles.searchLabel")}
            placeholder={t("articles.searchPlaceholder")}
            value={term}
            onChange={(event) => narrow(() => setTerm(event.target.value))}
          />
        </div>
        <MultiSelect
          label={t("articles.column.publication")}
          allLabel={t("pricing.all")}
          options={[
            { value: "published", label: t("article.status.published") },
            { value: "draft", label: t("article.status.draft") },
            { value: "archived", label: t("article.status.archived") },
          ]}
          selected={statuses}
          onChange={(next) => narrow(() => setStatuses(next))}
          {...multi}
        />
        <MultiSelect
          label={t("articles.column.active")}
          allLabel={t("pricing.all")}
          options={[
            { value: "true", label: t("article.active.active") },
            { value: "false", label: t("article.active.inactive") },
          ]}
          selected={actives}
          onChange={(next) => narrow(() => setActives(next))}
          {...multi}
        />
        <MultiSelect
          label={t("articles.companyFilter")}
          allLabel={t("articles.allCompanies")}
          options={(companyList.data?.items ?? []).map((entry) => ({
            value: entry.code,
            label: entry.name,
          }))}
          selected={companies}
          onChange={(next) => narrow(() => setCompanies(next))}
          {...multi}
        />
      </Toolbar>

      {mayWrite && selected.size > 0 ? (
        <Toolbar
          actions={
            <>
              <Select
                aria-label={t("articles.chooseBrand")}
                value={bulkBrand}
                onChange={(event) => setBulkBrand(event.target.value)}
              >
                <option value="">{t("articles.noBrandOption")}</option>
                {(brandChoices.data ?? []).map((label) => (
                  <option key={label} value={label}>
                    {label}
                  </option>
                ))}
              </Select>
              <Button
                variant="primary"
                busy={action.isBusy("bulk-brand")}
                onClick={() => {
                  void action.run("bulk-brand", async () => {
                    try {
                      const result = await api.setArticlesBrand([...selected], bulkBrand);
                      toast.show(t("articles.brandSet", { count: result.updated }), {
                        tone: "success",
                      });
                      setSelected(new Set());
                      articles.reload();
                    } catch {
                      toast.show(t("error.title"), { tone: "danger" });
                    }
                  });
                }}
              >
                {t("articles.setBrand")}
              </Button>
              <Button variant="ghost" onClick={() => setSelected(new Set())}>
                {t("articles.clearSelection")}
              </Button>
            </>
          }
        >
          <span>{t("articles.selectedCount", { count: selected.size })}</span>
        </Toolbar>
      ) : null}

      <DataTable<ArticleRow>
        caption={t("articles.caption")}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.article.id}
        loading={articles.loading}
        loadingLabel={t("articles.loading")}
        error={
          articles.failed
            ? { title: t("error.title"), onRetry: articles.reload, retryLabel: t("error.retry") }
            : undefined
        }
        empty={
          isNarrowed
            ? { title: t("articles.filtered.title"), description: t("articles.filtered.body") }
            : { title: t("articles.empty.title"), description: t("articles.empty.body") }
        }
      />

      {/* Cursor paging: "next" and "back to the start", because the API pages
          by keyset and there is no such thing as page 7. */}
      {articles.data?.next_cursor || cursor ? (
        <Toolbar
          actions={
            <>
              <Button onClick={() => setCursor(null)} disabled={cursor === null}>
                {t("pricing.all")}
              </Button>
              <Button
                variant="primary"
                onClick={() => setCursor(articles.data?.next_cursor ?? null)}
                disabled={!articles.data?.next_cursor}
              >
                →
              </Button>
            </>
          }
        />
      ) : null}
    </Page>
  );
}
